import { useState } from "react";
import type { Order } from "@/lib/projects";
import { formatHours } from "@/lib/bookedHours";
import { BookedHoursDetailModal } from "@/projectOverview/BookedHoursDetailModal";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { useOrderBookedHours } from "@/projectOverview/useOrderBookedHours";

export function BookedHoursSection({
  project,
  canRebook = false,
  onChanged,
}: {
  project: Order;
  canRebook?: boolean;
  onChanged?: () => void;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const {
    loading,
    error,
    totalHours,
    rows,
    heroError,
    subcontractorError,
    employeeError,
    refetch,
  } = useOrderBookedHours(project);

  const hasPartialError = heroError || subcontractorError || employeeError;
  const showTotal = !loading && !error;

  return (
    <>
      <OverviewCard title="Gebuchte Stunden">
        <div className="eh-po-hours-usage">
          <div className="eh-po-hours-usage-stats">
            <div className="eh-po-hours-usage-stat">
              <span className="eh-po-hours-usage-label">Summe</span>
              <strong className="eh-po-hours-usage-value">
                {loading ? "…" : error ? "–" : formatHours(totalHours)}
              </strong>
            </div>
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="text-sm font-medium text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
                onClick={() => setDetailOpen(true)}
              >
                Details anzeigen
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-label-tertiary">Lade gebuchte Stunden …</p>
          ) : null}

          {error ? (
            <p className="text-sm text-status-red">
              Gebuchte Stunden konnten nicht geladen werden.
            </p>
          ) : null}

          {showTotal && hasPartialError ? (
            <p className="text-xs text-status-orange">
              Einige Zeiten konnten nicht geladen werden — die Summe kann unvollständig sein.
            </p>
          ) : null}

          {showTotal && !rows.length ? (
            <p className="text-sm text-label-tertiary">
              Noch keine Stunden auf diesen Auftrag gebucht.
            </p>
          ) : null}
        </div>
      </OverviewCard>

      <BookedHoursDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        auftragsnummer={project.auftragsnummer}
        projectId={project.id}
        canRebook={canRebook}
        onRebooked={() => {
          void refetch({ silent: true });
          onChanged?.();
        }}
        onUpdated={() => {
          void refetch({ silent: true });
          onChanged?.();
        }}
        rows={rows}
        totalHours={totalHours}
        loading={loading}
        error={error}
        heroError={heroError}
        subcontractorError={subcontractorError}
        employeeError={employeeError}
      />
    </>
  );
}
